import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import React from 'react'
import {
  StatesProps,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import { OptionItem } from 'components/elements/common'
import { DestinationTypes, QnStatesProps, FuelTypes } from 'states/QnState'
import { success } from 'utils/status'
import { initialQnEditState } from '../../../../../states/QnState'

const fuelTypeOptions = Object.entries(VEHICLE_STATE_FORM.fuelType.options).map(
  ([k, v]) =>
    ({
      label: v as FuelTypes,
      value: k,
    } as OptionItem<FuelTypes>)
)

/**
 * QN-03-f: 燃料切れ
 */
const EmptyFuel: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()

  // バリデーション
  const isDisabled = React.useMemo(() => {
    const { fuelType, destinationType } = editState.vehicleStateInfo
    if (
      fuelType == 'electricity' &&
      destinationType === DestinationTypes.specify
    ) {
      return !editState.destinationLocation.isSelected
    }
    return false
  }, [editState.vehicleStateInfo, editState.destinationLocation])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    if (editState.vehicleStateInfo.fuelType == 'electricity') {
      switch (editState.vehicleStateInfo.destinationType) {
        case DestinationTypes.specify:
          return WARNING.WRECKER
        case DestinationTypes.introduction:
          return WARNING.INTRODUCTION
      }
    }
    return ''
  }, [
    editState.vehicleStateInfo.destinationType,
    editState.vehicleStateInfo.fuelType,
  ])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo((): string => {
    return props.state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : PAGE_PATH.TROUBLE_PLACE
  }, [])

  const handleFuelTypeChanged = React.useCallback(
    (value: FuelTypes) => {
      setVehicleStateInfo({
        fuelType: value,
      })
    },
    [setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    if (state.confirmationChangeType) {
      setEditingItem({
        ...editState,
        vehicleStateInfo: state.vehicleStateInfo,
        troubleDatetime: state.troubleDatetime,
        vehicleState: state.vehicleState,
        destinationLocation: state.destinationLocation,
      })
    } else {
      setState({
        ...state,
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      })
      setEditingItem({
        ...editState,
        vehicleStateInfo: initialQnEditState.vehicleStateInfo,
        destinationLocation: initialQnEditState.destinationLocation,
      })
    }
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.destinationLocation,
  ])

  return (
    <RequestTemplate
      title={'燃料切れ'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.fuelType.formName}の選択`}
            isRequired={true}
            options={fuelTypeOptions}
            selectedValue={editState.vehicleStateInfo.fuelType}
            onValueChanged={handleFuelTypeChanged}
          />
          {editState.vehicleStateInfo.fuelType == 'electricity' && (
            <>
              <SelectVehicleModificationContent />
              <SelectDestinationContent />
            </>
          )}
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      notClearEditingItem
      isDisabled={isDisabled}
    />
  )
}

export default EmptyFuel
